import {
  inviteUserInputSchema,
  ROLE_LABELS,
  userAccessInputSchema,
  userFormOptionsSchema,
  type InviteUserInput,
  type OrgUser,
  type UserAccessInput,
} from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

type Option = { id: string; label: string; depth: number; note?: string };

type Props = {
  organizationId: string;
  /** Sem `initial`: convite (pede o e-mail). Com `initial`: edição do acesso. */
  initial?: OrgUser;
  /** Equipe Fluxus: sem áreas e unidades (a organização Fluxus não tem estrutura de cliente). */
  team?: boolean;
  onSubmit: (input: InviteUserInput | UserAccessInput) => Promise<unknown>;
  onCancel: () => void;
  onBusyChange?: (busy: boolean) => void;
};

export const userOptionsKey = (organizationId: string) => ['users', organizationId, 'options'];

/** Convite ou edição de acesso: nome, e-mail (só no convite), perfil, áreas e unidades. */
export function UserForm({ organizationId, initial, team, onSubmit, onCancel, onBusyChange }: Props) {
  const { client } = useAuth();
  const me = useCurrentUser();
  const editingSelf = initial?.id === me.id;
  const [name, setName] = useState(initial?.name ?? '');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState(initial?.role.id ?? '');
  const [departmentIds, setDepartmentIds] = useState<string[]>(initial?.departments.map((d) => d.id) ?? []);
  const [businessUnitIds, setBusinessUnitIds] = useState<string[]>(
    initial?.businessUnits.map((b) => b.id) ?? [],
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { name: useId(), email: useId(), role: useId(), deps: useId(), units: useId() };

  const options = useQuery({
    queryKey: userOptionsKey(organizationId),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/users/options`, {
        schema: userFormOptionsSchema,
        signal,
      }),
  });

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const base = { name, roleId, departmentIds, businessUnitIds };
    const parsed = initial
      ? userAccessInputSchema.safeParse(base)
      : inviteUserInputSchema.safeParse({ ...base, email });
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    setSaving(true);
    onBusyChange?.(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  const clear = (field: string) => setErrors((e) => ({ ...e, [field]: undefined }));

  if (options.isPending) return <LoadingState rows={4} label="Carregando perfis e áreas" />;
  if (options.isError) {
    return (
      <ErrorState
        error={options.error}
        onRetry={() => void options.refetch()}
        retrying={options.isFetching}
      />
    );
  }

  const opts = options.data;
  // Áreas em árvore (indentadas pelo nível), mais as que a pessoa já tem e foram arquivadas depois.
  const ordered: Option[] = [];
  const isRoot = (parentId: string | null) => !parentId || !opts.departments.some((p) => p.id === parentId);
  const visit = (parentId: string | null, depth: number) => {
    for (const d of opts.departments.filter((x) =>
      parentId ? x.parentId === parentId : isRoot(x.parentId),
    )) {
      ordered.push({ id: d.id, label: d.name, depth });
      visit(d.id, depth + 1);
    }
  };
  visit(null, 0);
  for (const d of initial?.departments ?? []) {
    if (!ordered.some((o) => o.id === d.id))
      ordered.push({ id: d.id, label: d.name, depth: 0, note: 'arquivada' });
  }
  const units: Option[] = opts.businessUnits.map((u) => ({
    id: u.id,
    label: u.code ? `${u.name} (${u.code})` : u.name,
    depth: 0,
  }));
  for (const u of initial?.businessUnits ?? []) {
    if (!units.some((o) => o.id === u.id))
      units.push({ id: u.id, label: u.name, depth: 0, note: 'arquivada' });
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={120}
          autoComplete="off"
          onChange={(e) => {
            setName(e.target.value);
            clear('name');
          }}
          className="h-11"
        />
      </FormField>
      {initial ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">E-mail</span>
          <p className="text-sm break-all text-muted-foreground">{initial.email}</p>
        </div>
      ) : (
        <FormField
          id={ids.email}
          label="E-mail"
          error={errors.email}
          hint={
            <p className="text-xs text-muted-foreground">
              O convite com o link para criar a senha vai para este e-mail.
            </p>
          }
        >
          <Input
            {...fieldA11y(ids.email, errors.email, true)}
            type="email"
            inputMode="email"
            autoComplete="off"
            value={email}
            maxLength={254}
            onChange={(e) => {
              setEmail(e.target.value);
              clear('email');
            }}
            className="h-11"
          />
        </FormField>
      )}
      <FormField
        id={ids.role}
        label="Perfil"
        error={errors.roleId}
        hint={
          editingSelf ? (
            <p className="text-xs text-muted-foreground">
              Você não pode mudar o próprio perfil: peça a outro administrador.
            </p>
          ) : undefined
        }
      >
        <NativeSelect
          {...fieldA11y(ids.role, errors.roleId, editingSelf)}
          disabled={editingSelf}
          value={roleId}
          onChange={(e) => {
            setRoleId(e.target.value);
            clear('roleId');
          }}
        >
          <option value="">Escolha o perfil</option>
          {opts.roles
            .filter((r) => r.key !== 'FLUXUS_ADMIN' || me.role.key === 'FLUXUS_ADMIN' || r.id === roleId)
            .map((r) => (
              <option key={r.id} value={r.id}>
                {r.name || ROLE_LABELS[r.key]}
              </option>
            ))}
        </NativeSelect>
      </FormField>
      {team ? null : (
        <>
          <CheckList
            id={ids.deps}
            legend="Áreas"
            empty="Esta empresa ainda não tem áreas cadastradas."
            hint="Gerentes e Operação só veem o que é das suas áreas e unidades."
            items={ordered}
            selected={departmentIds}
            error={errors.departmentIds}
            onChange={(next) => {
              setDepartmentIds(next);
              clear('departmentIds');
            }}
          />
          <CheckList
            id={ids.units}
            legend="Unidades de negócio"
            empty="Esta empresa ainda não tem unidades cadastradas."
            items={units}
            selected={businessUnitIds}
            error={errors.businessUnitIds}
            onChange={(next) => {
              setBusinessUnitIds(next);
              clear('businessUnitIds');
            }}
          />
        </>
      )}
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar acesso' : 'Enviar convite'}
        </Button>
      </div>
    </form>
  );
}

function CheckList({
  id,
  legend,
  hint,
  empty,
  items,
  selected,
  error,
  onChange,
}: {
  id: string;
  legend: string;
  hint?: string;
  empty: string;
  items: Option[];
  selected: string[];
  error?: string;
  onChange: (next: string[]) => void;
}) {
  const toggle = (itemId: string, on: boolean) =>
    onChange(on ? [...selected, itemId] : selected.filter((x) => x !== itemId));
  return (
    <fieldset
      aria-describedby={
        [hint ? `${id}-dica` : null, error ? `${id}-erro` : null].filter(Boolean).join(' ') || undefined
      }
      aria-invalid={error ? true : undefined}
    >
      <legend className="text-sm font-medium">
        {legend}
        {selected.length ? (
          <span className="ml-2 font-normal text-muted-foreground">
            {selected.length === 1 ? '1 escolhida' : `${selected.length} escolhidas`}
          </span>
        ) : null}
      </legend>
      {hint ? (
        <p id={`${id}-dica`} className="mt-1 text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="mt-2 max-h-44 overflow-y-auto rounded-md border border-border p-1">
          {items.map((item) => (
            <li key={item.id}>
              <label
                className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded px-2 text-sm hover:bg-muted"
                style={{ paddingLeft: `${0.5 + item.depth * 1.25}rem` }}
              >
                <input
                  type="checkbox"
                  className="size-4 shrink-0 accent-(--brand)"
                  checked={selected.includes(item.id)}
                  onChange={(e) => toggle(item.id, e.target.checked)}
                />
                <span className="min-w-0">
                  {item.label}
                  {item.note ? (
                    <span className="ml-1 text-xs text-muted-foreground">({item.note})</span>
                  ) : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {error ? (
        <p id={`${id}-erro`} className="mt-1.5 text-sm font-medium text-status-red-fg">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
